// skipcq: JS-W1028, JS-0128
import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import useRecentAwardedSplats from '../../hooks/useRecentAwardedSplats';
import { getSplatTypeLabel } from '../../domain/splatConstants';

function splatAwardCount(splat) {
  const n = Number(splat.awardedCount ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function formatAwardedDate(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
}

function SplatDefinitionsHeadRow() {
  return (
    <TableRow>
      <TableCell>Name</TableCell>
      <TableCell>Type</TableCell>
      <TableCell align="right">Awarded count</TableCell>
      <TableCell align="right">Limit</TableCell>
      <TableCell>Active</TableCell>
    </TableRow>
  );
}

function SplatDefinitionRow({ splat }) {
  return (
    <TableRow hover>
      <TableCell>{splat.name ?? ''}</TableCell>
      <TableCell>{getSplatTypeLabel(splat.type)}</TableCell>
      <TableCell align="right">
        {splatAwardCount(splat).toLocaleString()}
      </TableCell>
      <TableCell align="right">{splat.limit ?? 0}</TableCell>
      <TableCell>{splat.isActive ? 'Yes' : 'No'}</TableCell>
    </TableRow>
  );
}

function SplatDefinitionsTable({ rows }) {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <SplatDefinitionsHeadRow />
        </TableHead>
        <TableBody>
          {rows.map((splat) => (
            <SplatDefinitionRow key={splat.id} splat={splat} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RecentAwardsHeadRow() {
  return (
    <TableRow>
      <TableCell>Awarded</TableCell>
      <TableCell>Splat</TableCell>
      <TableCell>User</TableCell>
    </TableRow>
  );
}

function RecentAwardRow({ row }) {
  return (
    <TableRow hover>
      <TableCell>{formatAwardedDate(row.awardedDate)}</TableCell>
      <TableCell>{row.splatName}</TableCell>
      <TableCell>{row.userDisplayName}</TableCell>
    </TableRow>
  );
}

function RecentAwardsTable({ recentAwards }) {
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <RecentAwardsHeadRow />
        </TableHead>
        <TableBody>
          {recentAwards.map((row) => (
            <RecentAwardRow key={row.id} row={row} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Read-only summary of splat awards: total awarded and per-definition counts.
 */
function SplatAwardsTab({ splats, loading, error }) {
  const {
    rows: recentAwards,
    loading: recentLoading,
    error: recentError,
  } = useRecentAwardedSplats(10);

  const rows = useMemo(() => {
    return [...splats].sort(
      (a, b) => splatAwardCount(b) - splatAwardCount(a),
    );
  }, [splats]);

  const totalAwarded = useMemo(
    () => rows.reduce((sum, s) => sum + splatAwardCount(s), 0),
    [rows],
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '40vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Total splats awarded (all definitions):{' '}
        <Typography component="span" variant="h6" color="primary">
          {totalAwarded.toLocaleString()}
        </Typography>
      </Typography>

      {rows.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          No splat definitions found.
        </Typography>
      ) : (
        <SplatDefinitionsTable rows={rows} />
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 1.5 }}>
        Recent splat awards
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Last 10 awards by date.
      </Typography>

      {recentError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {recentError}
        </Alert>
      )}

      {recentLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={32} />
        </Box>
      ) : recentAwards.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No awarded splat records yet.
        </Typography>
      ) : (
        <RecentAwardsTable recentAwards={recentAwards} />
      )}
    </Box>
  );
}

export default SplatAwardsTab;
